import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Save, X } from 'lucide-react';

import { TOOLS, DEFAULT_PIXEL_TO_MM } from './annotator/annotatorConstants';
import AnnotatorToolbar from './annotator/AnnotatorToolbar';
import AnnotatorSettingsPanel from './annotator/AnnotatorSettingsPanel';

const ImageAnnotator = ({
  imageUrl,
  imageId,
  initialAnnotations = [],
  onSave,
  onClose,
  isOpen
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [tool, setTool] = useState(TOOLS.RECTANGLE);
  const [color, setColor] = useState('#EF4444');
  const [lineWidth, setLineWidth] = useState(2);
  const [annotations, setAnnotations] = useState(initialAnnotations);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [image, setImage] = useState(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState(null);
  const [showTextDialog, setShowTextDialog] = useState(false);
  const [pixelToMm] = useState(DEFAULT_PIXEL_TO_MM);
  const [anglePoints, setAnglePoints] = useState([]);

  const calculateDistance = useCallback((x1, y1, x2, y2) => {
    const pixels = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    return (pixels * pixelToMm).toFixed(1);
  }, [pixelToMm]);

  const calculateAngle = useCallback((p1, p2, p3) => {
    const angle1 = Math.atan2(p1.y - p2.y, p1.x - p2.x);
    const angle2 = Math.atan2(p3.y - p2.y, p3.x - p2.x);
    let angle = Math.abs(angle1 - angle2) * (180 / Math.PI);
    if (angle > 180) angle = 360 - angle;
    return angle.toFixed(1);
  }, []);

  // Load image
  useEffect(() => {
    if (imageUrl && isOpen) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        setImage(img);
        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth - 40;
          const containerHeight = containerRef.current.clientHeight - 40;
          const scaleX = containerWidth / img.width;
          const scaleY = containerHeight / img.height;
          const newScale = Math.min(scaleX, scaleY, 1);
          setScale(newScale);
          setOffset({
            x: (containerWidth - img.width * newScale) / 2,
            y: (containerHeight - img.height * newScale) / 2
          });
        }
      };
      img.src = imageUrl;
    }
  }, [imageUrl, isOpen]);

  // Draw canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return;

    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(offset.x, offset.y);
    ctx.scale(scale, scale);
    ctx.drawImage(image, 0, 0);
    ctx.restore();

    [...annotations, currentAnnotation].filter(Boolean).forEach(ann => {
      ctx.save();
      ctx.translate(offset.x, offset.y);
      ctx.scale(scale, scale);
      ctx.strokeStyle = ann.color;
      ctx.fillStyle = ann.color;
      ctx.lineWidth = ann.lineWidth / scale;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      switch (ann.type) {
        case TOOLS.RECTANGLE:
          ctx.strokeRect(ann.x, ann.y, ann.width, ann.height);
          break;
        case TOOLS.CIRCLE: {
          ctx.beginPath();
          const radiusX = Math.abs(ann.width) / 2;
          const radiusY = Math.abs(ann.height) / 2;
          ctx.ellipse(ann.x + ann.width / 2, ann.y + ann.height / 2, radiusX, radiusY, 0, 0, 2 * Math.PI);
          ctx.stroke();
          break;
        }
        case TOOLS.FREEHAND:
          if (ann.points && ann.points.length > 1) {
            ctx.beginPath();
            ctx.moveTo(ann.points[0].x, ann.points[0].y);
            ann.points.forEach(point => ctx.lineTo(point.x, point.y));
            ctx.stroke();
          }
          break;
        case TOOLS.ARROW:
          if (ann.endX !== undefined) {
            ctx.beginPath();
            ctx.moveTo(ann.x, ann.y);
            ctx.lineTo(ann.endX, ann.endY);
            ctx.stroke();
            const arrowAngle = Math.atan2(ann.endY - ann.y, ann.endX - ann.x);
            const headLength = 15 / scale;
            ctx.beginPath();
            ctx.moveTo(ann.endX, ann.endY);
            ctx.lineTo(ann.endX - headLength * Math.cos(arrowAngle - Math.PI / 6), ann.endY - headLength * Math.sin(arrowAngle - Math.PI / 6));
            ctx.moveTo(ann.endX, ann.endY);
            ctx.lineTo(ann.endX - headLength * Math.cos(arrowAngle + Math.PI / 6), ann.endY - headLength * Math.sin(arrowAngle + Math.PI / 6));
            ctx.stroke();
          }
          break;
        case TOOLS.RULER:
          if (ann.endX !== undefined) {
            ctx.beginPath();
            ctx.moveTo(ann.x, ann.y);
            ctx.lineTo(ann.endX, ann.endY);
            ctx.stroke();
            const endpointRadius = 4 / scale;
            ctx.beginPath(); ctx.arc(ann.x, ann.y, endpointRadius, 0, 2 * Math.PI); ctx.fill();
            ctx.beginPath(); ctx.arc(ann.endX, ann.endY, endpointRadius, 0, 2 * Math.PI); ctx.fill();
            const midX = (ann.x + ann.endX) / 2;
            const midY = (ann.y + ann.endY) / 2;
            const distance = ann.measurement || calculateDistance(ann.x, ann.y, ann.endX, ann.endY);
            ctx.font = `bold ${14 / scale}px Arial`;
            ctx.fillStyle = '#000000';
            ctx.fillRect(midX - 25 / scale, midY - 12 / scale, 50 / scale, 16 / scale);
            ctx.fillStyle = ann.color;
            ctx.textAlign = 'center';
            ctx.fillText(`${distance}mm`, midX, midY);
            ctx.textAlign = 'left';
          }
          break;
        case TOOLS.ANGLE:
          if (ann.points && ann.points.length >= 2) {
            ctx.beginPath();
            ctx.moveTo(ann.points[0].x, ann.points[0].y);
            for (let i = 1; i < ann.points.length; i++) {
              ctx.lineTo(ann.points[i].x, ann.points[i].y);
            }
            ctx.stroke();
            const ptRadius = 4 / scale;
            ann.points.forEach(pt => { ctx.beginPath(); ctx.arc(pt.x, pt.y, ptRadius, 0, 2 * Math.PI); ctx.fill(); });
            if (ann.points.length === 3) {
              const angleValue = ann.angle || calculateAngle(ann.points[0], ann.points[1], ann.points[2]);
              ctx.font = `bold ${14 / scale}px Arial`;
              const textX = ann.points[1].x + 10 / scale;
              const textY = ann.points[1].y - 10 / scale;
              ctx.fillStyle = '#000000';
              ctx.fillRect(textX - 5 / scale, textY - 12 / scale, 40 / scale, 16 / scale);
              ctx.fillStyle = ann.color;
              ctx.fillText(`${angleValue}\u00B0`, textX, textY);
            }
          }
          break;
        case TOOLS.TEXT:
          ctx.font = `${16 / scale}px Arial`;
          ctx.fillText(ann.text, ann.x, ann.y);
          break;
        default:
          break;
      }
      ctx.restore();
    });
  }, [image, annotations, currentAnnotation, scale, offset, calculateDistance, calculateAngle]);

  useEffect(() => { drawCanvas(); }, [drawCanvas]);

  const getImagePosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - offset.x) / scale,
      y: (e.clientY - rect.top - offset.y) / scale
    };
  };

  const handleMouseDown = (e) => {
    if (tool === TOOLS.SELECT) return;
    const pos = getImagePosition(e);

    if (tool === TOOLS.TEXT) {
      setTextPosition(pos);
      setShowTextDialog(true);
      return;
    }

    if (tool === TOOLS.ANGLE) {
      const newPoints = [...anglePoints, pos];
      setAnglePoints(newPoints);
      if (newPoints.length === 3) {
        setAnnotations(prev => [...prev, {
          id: Date.now(), type: TOOLS.ANGLE, color, lineWidth, points: newPoints,
          angle: calculateAngle(newPoints[0], newPoints[1], newPoints[2]),
          label: `Angle: ${calculateAngle(newPoints[0], newPoints[1], newPoints[2])}\u00B0`
        }]);
        setAnglePoints([]);
      } else {
        setCurrentAnnotation({ id: Date.now(), type: TOOLS.ANGLE, color, lineWidth, points: newPoints, label: '' });
      }
      return;
    }

    setIsDrawing(true);
    const newAnnotation = { id: Date.now(), type: tool, color, lineWidth, x: pos.x, y: pos.y, label: '' };
    if (tool === TOOLS.FREEHAND) newAnnotation.points = [pos];
    setCurrentAnnotation(newAnnotation);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !currentAnnotation) return;
    const pos = getImagePosition(e);
    setCurrentAnnotation(prev => {
      const updated = { ...prev };
      switch (tool) {
        case TOOLS.RECTANGLE:
        case TOOLS.CIRCLE:
          updated.width = pos.x - prev.x;
          updated.height = pos.y - prev.y;
          break;
        case TOOLS.FREEHAND:
          updated.points = [...(prev.points || []), pos];
          break;
        case TOOLS.ARROW:
        case TOOLS.RULER:
          updated.endX = pos.x;
          updated.endY = pos.y;
          if (tool === TOOLS.RULER) updated.measurement = calculateDistance(prev.x, prev.y, pos.x, pos.y);
          break;
        default:
          break;
      }
      return updated;
    });
  };

  const handleMouseUp = () => {
    if (isDrawing && currentAnnotation) {
      const hasSize =
        (currentAnnotation.width && Math.abs(currentAnnotation.width) > 5) ||
        (currentAnnotation.height && Math.abs(currentAnnotation.height) > 5) ||
        (currentAnnotation.points && currentAnnotation.points.length > 2) ||
        (currentAnnotation.endX !== undefined);
      if (hasSize) {
        if (currentAnnotation.type === TOOLS.RULER && currentAnnotation.measurement) {
          currentAnnotation.label = `Measurement: ${currentAnnotation.measurement}mm`;
        }
        setAnnotations(prev => [...prev, currentAnnotation]);
      }
    }
    setCurrentAnnotation(null);
    setIsDrawing(false);
  };

  const handleTextSubmit = () => {
    if (textInput.trim() && textPosition) {
      setAnnotations(prev => [...prev, {
        id: Date.now(), type: TOOLS.TEXT, color, lineWidth,
        x: textPosition.x, y: textPosition.y, text: textInput.trim(), label: textInput.trim()
      }]);
    }
    setTextInput('');
    setTextPosition(null);
    setShowTextDialog(false);
  };

  const getAnnotationDescription = (ann) => {
    const typeNames = {
      [TOOLS.RECTANGLE]: 'rectangular region', [TOOLS.CIRCLE]: 'circular region',
      [TOOLS.FREEHAND]: 'freehand outline', [TOOLS.ARROW]: 'arrow pointing to',
      [TOOLS.TEXT]: 'text label', [TOOLS.RULER]: 'measurement', [TOOLS.ANGLE]: 'angle measurement'
    };
    let desc = `${typeNames[ann.type] || 'region'} at approximately `;
    if (image) {
      desc += `${Math.round((ann.x / image.width) * 100)}% from left, ${Math.round((ann.y / image.height) * 100)}% from top`;
    }
    if (ann.measurement) desc += ` - ${ann.measurement}mm`;
    if (ann.angle) desc += ` - ${ann.angle}\u00B0`;
    if (ann.text) desc += `: "${ann.text}"`;
    return desc;
  };

  const handleSave = () => {
    const annotationsWithLabels = annotations.map((ann, idx) => ({
      ...ann,
      label: ann.label || `Region ${idx + 1}`,
      description: getAnnotationDescription(ann)
    }));
    onSave(annotationsWithLabels);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full flex flex-col p-0">
        <DialogHeader className="p-4 border-b border-[#E5E4E0]">
          <DialogTitle className="font-['Work_Sans']">Annotate Image</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          <AnnotatorToolbar
            tool={tool}
            setTool={setTool}
            onZoomIn={() => setScale(prev => Math.min(prev * 1.2, 3))}
            onZoomOut={() => setScale(prev => Math.max(prev / 1.2, 0.3))}
            onUndo={() => { setAnnotations(prev => prev.slice(0, -1)); setAnglePoints([]); }}
            onClear={() => { setAnnotations([]); setAnglePoints([]); }}
            annotationCount={annotations.length}
          />

          <div ref={containerRef} className="flex-1 bg-black overflow-hidden relative">
            <canvas
              ref={canvasRef}
              width={containerRef.current?.clientWidth || 800}
              height={containerRef.current?.clientHeight || 600}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              className="cursor-crosshair"
              data-testid="annotation-canvas"
            />
          </div>

          <AnnotatorSettingsPanel
            color={color}
            setColor={setColor}
            lineWidth={lineWidth}
            setLineWidth={setLineWidth}
            annotationCount={annotations.length}
          />
        </div>

        <DialogFooter className="p-4 border-t border-[#E5E4E0] flex justify-between">
          <Button variant="outline" onClick={onClose} className="border-[#E5E4E0]">
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} className="bg-[#4B6651] hover:bg-[#3E5543] text-white" data-testid="save-annotations">
            <Save className="w-4 h-4 mr-2" />
            Save Annotations ({annotations.length})
          </Button>
        </DialogFooter>

        <Dialog open={showTextDialog} onOpenChange={setShowTextDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Add Text Label</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <Input
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder="Enter label text..."
                className="bg-[#F8F7F4] border-[#E5E4E0]"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && handleTextSubmit()}
                data-testid="text-label-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTextDialog(false)}>Cancel</Button>
              <Button onClick={handleTextSubmit} className="bg-[#4B6651] hover:bg-[#3E5543] text-white">Add Label</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default ImageAnnotator;
